/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  type ApproveSessionRequestPayload,
  type CreateSessionRequestPayload,
  type SessionRequest,
  type SessionRequestStatus,
  sessionRequestsApi,
} from '../api/session-requests.js';

const KEY = ['session-requests'] as const;

function useInvalidate(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: KEY });
  };
}

export function useSessionRequests(
  status?: SessionRequestStatus,
): UseQueryResult<readonly SessionRequest[]> {
  return useQuery({
    queryKey: [...KEY, 'all', status ?? 'all'],
    queryFn: () => sessionRequestsApi.listAll(status),
  });
}

export function useMySessionRequests(): UseQueryResult<readonly SessionRequest[]> {
  return useQuery({ queryKey: [...KEY, 'mine'], queryFn: () => sessionRequestsApi.listMine() });
}

export function useCreateSessionRequest(): UseMutationResult<
  SessionRequest,
  Error,
  CreateSessionRequestPayload
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (p: CreateSessionRequestPayload) => sessionRequestsApi.create(p),
    onSuccess: invalidate,
  });
}

export function useApproveSessionRequest(): UseMutationResult<
  SessionRequest,
  Error,
  { id: string; payload: ApproveSessionRequestPayload }
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ApproveSessionRequestPayload }) =>
      sessionRequestsApi.approve(id, payload),
    onSuccess: invalidate,
  });
}

export function useDeclineSessionRequest(): UseMutationResult<
  SessionRequest,
  Error,
  { id: string; adminNote?: string }
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, adminNote }: { id: string; adminNote?: string }) =>
      sessionRequestsApi.decline(id, adminNote),
    onSuccess: invalidate,
  });
}
